import { useEffect, useRef } from "react";

import { tailwindBreakpoints } from "@/styles/design-tokens";
import { subscribeToMediaQuery } from "@/utils/match-media";

/**
 * Suppress the global Shopping Assistant on narrow viewports while a modal is open.
 *
 * This works by emitting `globalModalCountDelta` events that the app layout listens to.
 *
 * NOTE: This behavior should remain opt-in via this hook (instead of being baked into a modal shell).
 */
export function useGlobalAssistantSuppression(isOpen: boolean): void {
  const didIncrementRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === "undefined") return;

    // Only treat true mobile (< sm) as needing suppression; tablets follow desktop rules.
    // Listen for viewport changes while the modal is open (e.g., device rotation).
    const mql = window.matchMedia(`(max-width: ${tailwindBreakpoints.sm - 1}px)`);
    const sync = () => {
      const shouldSuppress = mql.matches;
      if (shouldSuppress && !didIncrementRef.current) {
        window.dispatchEvent(new CustomEvent("globalModalCountDelta", { detail: { delta: 1 } }));
        didIncrementRef.current = true;
        return;
      }
      if (!shouldSuppress && didIncrementRef.current) {
        window.dispatchEvent(new CustomEvent("globalModalCountDelta", { detail: { delta: -1 } }));
        didIncrementRef.current = false;
      }
    };

    sync();
    const unsub = subscribeToMediaQuery(mql, sync);

    return () => {
      unsub();
      if (didIncrementRef.current)
        window.dispatchEvent(new CustomEvent("globalModalCountDelta", { detail: { delta: -1 } }));
      didIncrementRef.current = false;
    };
  }, [isOpen]);
}
